import { gql } from "@apollo/client";
import {
  ArticleAdminPageData,
  ArticleAdminPageDataVariables,
} from "__generated__/types";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { usePreviousPath } from "common/context/PreviousPathContext";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { ADMIN_PREFIX, urls } from "common/urls";
import { adminArticleFragment } from "domain/admin/fragments";
import { urlByType } from "domain/common/url-utils";
import { getParentFromContentContext } from "domain/common/utils";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import React from "react";
import { useNavigate, useParams } from "react-router";
import { ArticleEditForm } from "../article/ArticleEditForm";

type RouteProps = {
  articleId: string;
};

const GET_ARTICLE = gql`
  query ArticleAdminPageData($articleId: ID!) {
    articleById(id: $articleId) {
      ...ContentBreadcrumbFragment
      ...AdminArticleFragment
    }
  }
  ${contentBreadcrumbFragment}
  ${adminArticleFragment}
`;

export const ArticleAdminPage: React.FC = () => {
  const { articleId } = useParams<RouteProps>();
  const navigate = useNavigate();
  const previousPath = usePreviousPath();

  if (!articleId) {
    throw new Error("Unexpected error: Missing articleId");
  }

  const { data, loadingOrError } = useSimpleQuery<
    ArticleAdminPageData,
    ArticleAdminPageDataVariables
  >(GET_ARTICLE, {
    variables: {
      articleId,
    },
  });

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.articleById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const { title, context = [] } = data.articleById;
  const parent = getParentFromContentContext(context);

  function exitEditPage() {
    if (previousPath && !previousPath.includes(ADMIN_PREFIX)) {
      navigate(-1);
    } else if (parent) {
      navigate(urlByType(parent.__typename, parent.id, true));
    } else {
      navigate(urls.admin);
    }
  }

  return (
    <AdministrationLayout title={title} context={context}>
      <ArticleEditForm
        article={data.articleById}
        cancel={exitEditPage}
        complete={exitEditPage}
      />
    </AdministrationLayout>
  );
};
